import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { 
  BarChart3, Users, School, BookOpen, Calendar, 
  TrendingUp, Award, FileText, MessageSquare,
  Brain, PieChart, Activity, Download, FileSpreadsheet
} from 'lucide-react';
import apiClient from '../api/client';

const Analytics = () => {
  const { user, isSuperAdmin } = useAuth();
  const [networkStats, setNetworkStats] = useState(null);
  const [courseStats, setCourseStats] = useState(null);
  const [aiStats, setAiStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [exporting, setExporting] = useState(null);

  useEffect(() => {
    document.title = 'Analitik | İlkadım360 Hub';
    loadAllStats();
  }, []);

  const loadAllStats = async () => {
    try {
      const [networkRes, courseRes, aiRes] = await Promise.all([
        apiClient.get('/stats/network'),
        apiClient.get('/stats/courses/overview'),
        apiClient.get('/stats/ai-usage')
      ]);
      setNetworkStats(networkRes.data);
      setCourseStats(courseRes.data);
      setAiStats(aiRes.data);
    } catch (error) {
      console.error('Stats yüklenemedi:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(format);
    try {
      const response = await apiClient.get(`/stats/export/${format}`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { 
        type: format === 'pdf' 
          ? 'application/pdf' 
          : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ilkadim360_rapor_${new Date().toISOString().slice(0,10)}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export hatası:', error);
      alert('Dışa aktarma sırasında hata oluştu');
    } finally {
      setExporting(null);
    }
  };

  if (!isSuperAdmin() && !user?.email?.endsWith('@yomev.org')) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-600">Bu sayfaya erişim yetkiniz yok.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Genel Bakış', icon: BarChart3 },
    { id: 'courses', label: 'Kurslar', icon: BookOpen },
    { id: 'ai', label: 'AI Kullanımı', icon: Brain },
  ];

  return (
    <div className="space-y-6" data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analitik & Raporlar</h1>
          <p className="text-gray-600 mt-1">YÖMEV Network istatistikleri ve performans metrikleri</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => handleExport('excel')}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="export-excel-btn"
          >
            {exporting === 'excel' ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <FileSpreadsheet size={18} />
            )}
            Excel
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            data-testid="export-pdf-btn"
          >
            {exporting === 'pdf' ? (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            ) : (
              <Download size={18} />
            )}
            PDF
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <tab.icon size={18} />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && networkStats && (
        <OverviewTab stats={networkStats} />
      )}
      {activeTab === 'courses' && courseStats && (
        <CoursesTab stats={courseStats} />
      )}
      {activeTab === 'ai' && aiStats && (
        <AIUsageTab stats={aiStats} />
      )}
    </div>
  );
};

// Overview Tab Component
const OverviewTab = ({ stats }) => (
  <div className="space-y-6">
    {/* Main Stats Grid */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard icon={School} label="Toplam Okul" value={stats.totalOrganizations} color="blue" />
      <StatCard icon={Users} label="Aktif Kullanıcı" value={stats.totalUsers} color="green" />
      <StatCard icon={Calendar} label="Ders Planı" value={stats.totalPlans} color="purple" />
      <StatCard icon={FileText} label="Haftalık Plan" value={stats.totalWeeklyPlans} color="indigo" />
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <StatCard icon={BookOpen} label="Toplam Kurs" value={stats.totalCourses} color="orange" />
      <StatCard icon={TrendingUp} label="Kayıt Sayısı" value={stats.totalEnrollments} color="teal" />
      <StatCard icon={MessageSquare} label="Community Post" value={stats.totalPosts} color="pink" />
      <StatCard icon={FileText} label="Kütüphane Dokümanı" value={stats.totalLibraryDocuments} color="cyan" />
    </div>

    {/* Two Column Layout */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Accreditation Applications */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Award className="text-yellow-500" size={20} />
          Akreditasyon Başvuruları
        </h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-3xl font-bold text-gray-900">{stats.accreditationApplications.total}</p>
            <p className="text-sm text-gray-500">Toplam Başvuru</p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-yellow-600">{stats.accreditationApplications.new}</p>
            <p className="text-sm text-gray-500">Yeni Başvuru</p>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <Activity className="text-green-500" size={20} />
          Son 30 Gün Aktivite
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <p className="text-2xl font-bold text-blue-600">{stats.recentActivity.weeklyPlans}</p>
            <p className="text-xs text-gray-600">Haftalık Plan</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <p className="text-2xl font-bold text-purple-600">{stats.recentActivity.plans}</p>
            <p className="text-xs text-gray-600">Ders Planı</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <p className="text-2xl font-bold text-green-600">{stats.recentActivity.newUsers}</p>
            <p className="text-xs text-gray-600">Yeni Kullanıcı</p>
          </div>
          <div className="text-center p-3 bg-pink-50 rounded-lg">
            <p className="text-2xl font-bold text-pink-600">{stats.recentActivity.posts}</p>
            <p className="text-xs text-gray-600">Paylaşım</p>
          </div>
        </div>
      </div>
    </div>

    {/* Accreditation Distribution & Top Orgs */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Accreditation Distribution */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <PieChart className="text-indigo-500" size={20} />
          Akreditasyon Dağılımı
        </h3>
        <div className="space-y-3">
          {stats.accreditationDistribution.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${getAccreditationColor(item.level)}`} />
                <span className="text-gray-700">{item.level}</span>
              </div>
              <span className="font-semibold">{item.count} okul</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Organizations */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
          <TrendingUp className="text-green-500" size={20} />
          En Aktif Okullar
        </h3>
        <div className="space-y-3">
          {stats.topOrganizations.map((org, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium">
                  {idx + 1}
                </span>
                <span className="text-gray-700">{org.name}</span>
              </div>
              <span className="text-sm font-medium text-gray-600">{org.planCount} plan</span>
            </div>
          ))}
          {stats.topOrganizations.length === 0 && (
            <p className="text-gray-500 text-center py-4">Henüz veri yok</p>
          )}
        </div>
      </div>
    </div>
  </div>
);

// Courses Tab Component
const CoursesTab = ({ stats }) => (
  <div className="space-y-6">
    {/* Main Stats */}
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <StatCard 
        icon={BookOpen} 
        label="Toplam Kayıt" 
        value={stats.enrollments.total} 
        color="blue" 
      />
      <StatCard 
        icon={Activity} 
        label="Aktif Kayıt" 
        value={stats.enrollments.active} 
        color="green" 
      />
      <StatCard 
        icon={Award} 
        label="Tamamlanan" 
        value={stats.enrollments.completed} 
        color="purple" 
      />
      <StatCard 
        icon={TrendingUp} 
        label="Ort. İlerleme" 
        value={`%${stats.averageProgress}`} 
        color="orange" 
      />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Course Status */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4">Kurs Durumları</h3>
        <div className="space-y-3">
          {stats.coursesByStatus.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <span className="text-gray-700">{item.status}</span>
              <span className="font-bold text-lg">{item.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Courses */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4">En Popüler Kurslar</h3>
        <div className="space-y-3">
          {stats.topCourses.map((course, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm font-medium">
                  {idx + 1}
                </span>
                <span className="text-gray-700 truncate max-w-[200px]">{course.title}</span>
              </div>
              <span className="text-sm font-medium text-gray-600">{course.enrollments} kayıt</span>
            </div>
          ))}
          {stats.topCourses.length === 0 && (
            <p className="text-gray-500 text-center py-4">Henüz veri yok</p>
          )}
        </div>
      </div>
    </div>

    {/* Completion Rate */}
    <div className="bg-white rounded-xl shadow-sm border p-6">
      <h3 className="text-lg font-semibold mb-4">Tamamlama Oranı</h3>
      <div className="flex items-center gap-4">
        <div className="flex-1 bg-gray-200 rounded-full h-4">
          <div 
            className="bg-green-500 h-4 rounded-full transition-all duration-500"
            style={{ width: `${stats.enrollments.completionRate}%` }}
          />
        </div>
        <span className="text-lg font-bold text-gray-700">%{stats.enrollments.completionRate}</span>
      </div>
    </div>
  </div>
);

// AI Usage Tab Component
const AIUsageTab = ({ stats }) => (
  <div className="space-y-6">
    {/* Main Stats */}
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <StatCard 
        icon={Brain} 
        label="Toplam AI Plan" 
        value={stats.totalWeeklyPlans} 
        color="purple" 
      />
      <StatCard 
        icon={Award} 
        label="KK Geçen" 
        value={stats.qualityAssurance.passedCount} 
        color="green" 
      />
      <StatCard 
        icon={TrendingUp} 
        label="Başarı Oranı" 
        value={`%${stats.qualityAssurance.passRate}`} 
        color="blue" 
      />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* By Age Group */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4">Yaş Grubuna Göre</h3>
        <div className="space-y-3">
          {stats.byAgeGroup.map((item, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <span className="text-gray-700">{item.ageGroup}</span>
              <span className="font-bold text-purple-600">{item.count} plan</span>
            </div>
          ))}
        </div>
      </div>

      {/* Top Themes */}
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4">Popüler Temalar</h3>
        <div className="space-y-2">
          {stats.topThemes.slice(0, 5).map((item, idx) => (
            <div key={idx} className="flex items-center justify-between p-2 hover:bg-gray-50 rounded-lg">
              <span className="text-gray-700 truncate max-w-[200px]">{item.theme}</span>
              <span className="text-sm font-medium bg-blue-100 text-blue-700 px-2 py-1 rounded">
                {item.count}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>

    {/* Monthly Trend */}
    {stats.monthlyTrend.length > 0 && (
      <div className="bg-white rounded-xl shadow-sm border p-6">
        <h3 className="text-lg font-semibold mb-4">Aylık Üretim Trendi</h3>
        <div className="flex items-end gap-2 h-32">
          {stats.monthlyTrend.map((item, idx) => {
            const maxCount = Math.max(...stats.monthlyTrend.map(m => m.count));
            const height = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
            return (
              <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                <div 
                  className="w-full bg-gradient-to-t from-purple-500 to-purple-300 rounded-t transition-all duration-500"
                  style={{ height: `${height}%`, minHeight: item.count > 0 ? '8px' : '0' }}
                />
                <span className="text-xs text-gray-500">{item.month.split('-')[1]}</span>
                <span className="text-xs font-medium">{item.count}</span>
              </div>
            );
          })}
        </div>
      </div>
    )}
  </div>
);

// Stat Card Component
const StatCard = ({ icon: Icon, label, value, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
    indigo: 'bg-indigo-100 text-indigo-600',
    teal: 'bg-teal-100 text-teal-600',
    pink: 'bg-pink-100 text-pink-600',
    cyan: 'bg-cyan-100 text-cyan-600',
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-5 hover:shadow-md transition-shadow">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon size={22} />
        </div>
      </div>
    </div>
  );
};

// Helper function for accreditation colors
const getAccreditationColor = (level) => {
  const colors = {
    'Yok': 'bg-gray-400',
    'Başlangıç': 'bg-yellow-400',
    'Gelişen': 'bg-blue-400',
    'İleri': 'bg-green-400'
  };
  return colors[level] || 'bg-gray-400';
};

export default Analytics;
